import * as React from 'react';
import styled from 'styled-components';
import { SearchOptions } from '@algolia/client-search';
import { themeGet } from '@packages/ui';
import { AppStaticStateContext, ClickoutProvider } from '@packages/shared';
import {
  Product,
  ModuleProductsV2,
  ShowLoadmore,
  ConcatenateIdsWithDynamicQueries,
  sortByList,
  SortingOrderOfIds,
  getAlgoliaIndice,
  PriceSortQueryValue,
  AlgoliaIndiceName,
  DeviceType,
  RecommendationAppearance,
  ClickoutSegment,
} from '@packages/common';
import { useProductSearchById, useProductSearch } from '@packages/services';

import { HeadlineWithPaddingBottom } from '../Headline/Headline';
import CMSSlider from '../CMSSlider/CMSSlider';
import { CmsProductGrid } from '../CmsProductGrid/CmsProductGrid';

export const MAX_HITS_PER_PAGE_FOR_RECOMMENDATIONS = 60;

export const Container = styled.div`
  max-width: ${themeGet.content('cmsModuleMaxWidth')};
  margin: 0 auto;
`;

export type ProductsProps = ModuleProductsV2;

interface ReduceFiltersParams {
  productIds?: string[];
  query?: string;
  filters?: string[];
  categoryId?: string;
  concatenateIdsWithDynamicQueries: string;
  algoliaSorting: any;
  deviceType: DeviceType;
}

// TODO: Refactor
const createQueryParams = ({
  query,
  categoryId,
  filters,
  productIds,
  concatenateIdsWithDynamicQueries,
  algoliaSorting,
  deviceType,
}: ReduceFiltersParams): { queryParameters: SearchOptions; indiceName: AlgoliaIndiceName } => {
  const querysort =
    algoliaSorting == 'price_ascending'
      ? PriceSortQueryValue.ASC
      : algoliaSorting == 'price_descending'
      ? PriceSortQueryValue.DESC
      : PriceSortQueryValue.NONE;
  const indiceName = getAlgoliaIndice(deviceType, querysort, AlgoliaIndiceName.PRODUCTS);

  const noFilterQueries = query == null && categoryId == null && filters == null;

  if (noFilterQueries) {
    //to bail
    return {
      queryParameters: {
        filters: 'productId:0',
      },
      indiceName,
    };
  }
  // logic : Algolia category ID+Algolia Filters+Algolia query
  const categoryFilter = `category:${categoryId ? categoryId : '0'}`;
  const queryFilter = filters && filters.length > 0 ? `${filters.join(' AND ')}` : '';
  //concatenateIdsWithDynamicQueries == yes means that the user wants to fill the
  // rest of the productsperpage after the productsIds specified with results from query.
  // and this should not use duplicate products. so we need to exclude the productIds specified
  const excludeProductIdsEntered =
    concatenateIdsWithDynamicQueries == ConcatenateIdsWithDynamicQueries.TRUE && productIds
      ? `NOT productId: ${productIds.join(' AND NOT productId:')}`
      : '';

  const algoliaFiltersArray = [categoryFilter, queryFilter, excludeProductIdsEntered].filter(
    (item) => item.trim() !== '',
  );

  return {
    queryParameters: {
      query,
      filters: algoliaFiltersArray.join(' AND '),
    },
    indiceName,
  };
};

export const Products: React.FC<ProductsProps> = (props) => {
  const {
    title,
    appearence,
    categoryId,
    productIds = [],
    sortingOrderOfIds,
    showLoadmore = ShowLoadmore.TRUE,
    concatenateIdsWithDynamicQueries,
    filters,
    algoliaSorting,
  } = props;
  const productsPerPage = props.productsPerPage || MAX_HITS_PER_PAGE_FOR_RECOMMENDATIONS;
  const query = props.query?.trim() || undefined;
  const { deviceType } = React.useContext(AppStaticStateContext);
  const [page, setPage] = React.useState(0);
  const [filterQueryProducts, setFilterQueryProducts] = React.useState<Product[]>([]);
  const [productIdQueryProducts, setProductIdQueryProducts] = React.useState<Product[]>([]);
  const [totalProductCount, setTotalProductCount] = React.useState(0);

  const [, , productIdSearchResponse] = useProductSearchById(
    productIds,
    'productId',
    false,
    productsPerPage * (page + 1),
  );
  const { queryParameters, indiceName } = createQueryParams({
    query,
    categoryId,
    concatenateIdsWithDynamicQueries,
    filters,
    productIds,
    algoliaSorting,
    deviceType,
  });

  const [, , filterSearchResponse] = useProductSearch({
    queryParameters: { ...queryParameters, hitsPerPage: productsPerPage * (page + 1) },
    deviceType,
    indiceName,
  });

  React.useEffect(() => {
    if (productIdSearchResponse) {
      setProductIdQueryProducts(
        sortingOrderOfIds == SortingOrderOfIds.MANUAL
          ? sortByList(productIdSearchResponse.hits, 'productId', productIds)
          : productIdSearchResponse.hits,
      );
    }
  }, [productIdSearchResponse, productIds, setProductIdQueryProducts, sortingOrderOfIds]);

  React.useEffect(() => {
    if (filterSearchResponse) {
      setFilterQueryProducts(filterSearchResponse.hits);
    }
  }, [filterSearchResponse]);

  React.useEffect(() => {
    setTotalProductCount((productIdSearchResponse?.nbHits || 0) + (filterSearchResponse?.nbHits || 0));
  }, [filterSearchResponse, productIdSearchResponse]);

  const handleUpdatePage = () => {
    setPage((page) => page + 1);
  };

  const splitLength = productsPerPage * (page + 1);
  const allContent = [...filterQueryProducts, ...productIdQueryProducts];
  const allContentReverse = [...productIdQueryProducts, ...filterQueryProducts];

  const [allProducts] =
    concatenateIdsWithDynamicQueries == ConcatenateIdsWithDynamicQueries.TRUE
      ? [allContentReverse.slice(0, splitLength), allContentReverse.slice(splitLength)]
      : [allContent.slice(0, splitLength), allContent.slice(splitLength)];

  return allProducts.length > 0 ? (
    <ClickoutProvider segment={ClickoutSegment.CONTENT} service="content">
      <Container>
        <HeadlineWithPaddingBottom>{title}</HeadlineWithPaddingBottom>
        {appearence === RecommendationAppearance.GRID ? (
          <CmsProductGrid
            hits={allProducts}
            handleUpdatePage={handleUpdatePage}
            totalResultsAvailable={totalProductCount}
            showLoadmore={showLoadmore === ShowLoadmore.TRUE}
            deviceType={deviceType}
            categoryId={categoryId ? categoryId : '0'}
          />
        ) : (
          <CMSSlider hits={allProducts} deviceType={deviceType} categoryId={categoryId} />
        )}
      </Container>
    </ClickoutProvider>
  ) : null;
};
